/**
 * Shared wire protocol between the realtime server and the web client.
 * Mostly compile-time-only types (erased at runtime), imported by both sides
 * via the `@xq/protocol` alias so the two can never drift. The time-control
 * table below is the one runtime value here: server and client must agree on
 * what "5+0" means, so it lives in exactly one place.
 */

export type Side = "red" | "black";

export interface Clocks {
  red: number; // ms remaining
  black: number;
}

export type GameStatus = "playing" | "red-wins" | "black-wins" | "draw";

// ---- Time controls ----
export type TimeControlId = "1+0" | "2+1" | "3+2" | "5+0" | "10+5" | "15+10" | "30+0";
export type TimeCategory = "bullet" | "blitz" | "rapid" | "classical";

export interface TimeControl {
  id: TimeControlId;
  /** starting clock, ms */
  initial: number;
  /** added after each of your own moves, ms */
  increment: number;
  cat: TimeCategory;
}

/** The offered pools, fastest first. Order is the order shown in the lobby. */
export const TIME_CONTROLS: readonly TimeControl[] = [
  { id: "1+0", initial: 60_000, increment: 0, cat: "bullet" },
  { id: "2+1", initial: 120_000, increment: 1_000, cat: "bullet" },
  { id: "3+2", initial: 180_000, increment: 2_000, cat: "blitz" },
  { id: "5+0", initial: 300_000, increment: 0, cat: "blitz" },
  { id: "10+5", initial: 600_000, increment: 5_000, cat: "rapid" },
  { id: "15+10", initial: 900_000, increment: 10_000, cat: "rapid" },
  { id: "30+0", initial: 1_800_000, increment: 0, cat: "classical" },
];

export const DEFAULT_TC: TimeControlId = "10+5";

/** Resolve an id off the wire; anything unknown falls back to the default pool. */
export function timeControl(id: string | undefined | null): TimeControl {
  return TIME_CONTROLS.find((c) => c.id === id) ?? TIME_CONTROLS.find((c) => c.id === DEFAULT_TC)!;
}

/** Colour a seeker asks to play; "random" matches anything. */
export type ColorPref = "red" | "black" | "random";

/** An open challenge sitting in the lobby, as shown to other players. */
export interface Seek {
  id: string;
  name: string;
  rating: number;
  tc: TimeControlId;
  rated: boolean;
  color: ColorPref;
  /** true when this seek belongs to the player receiving the message */
  mine: boolean;
}

/**
 * A game in progress, as listed on the Watch page.
 *
 * Only games from the public pools are ever listed. A game started from a
 * private room code is watchable by its link — a player may still share it —
 * but it never appears here, or a "private room" would not be one.
 */
export interface LiveGame {
  id: string;
  red: { name: string; user?: string; rating: number };
  black: { name: string; user?: string; rating: number };
  tc: TimeControlId;
  rated: boolean;
  /** plies played so far */
  ply: number;
  /** whose move it is */
  turn: Side;
  watchers: number;
  startedAt: number;
}

export interface GameSnapshot {
  gameId: string;
  fen: string;
  turn: Side;
  lastMove: { from: number; to: number } | null;
  /** Every move played so far, oldest first — lets a client review history even after reconnecting. */
  moves: { from: number; to: number }[];
  clocks: Clocks;
  status: GameStatus;
  /** why the game ended, when status !== 'playing' (checkmate/stalemate/resign/timeout/...) */
  endReason?: string;
  yourColor: Side;
  opponentName: string;
  /**
   * Both players by side. A player knows their own name already, but a watcher
   * is neither side and would otherwise only be told one of the two.
   */
  names?: { red: string; black: string };
  /** Account usernames by side, where the player has one (never for bots/guests). */
  users?: { red?: string; black?: string };
  /** The opponent's account username, when they have one (absent for guests and bots). */
  opponentUser?: string;
  /** The invite code, for a game that started from a private room. */
  code?: string;
  opponentConnected: boolean;
  /** ms increment added per move */
  increment: number;
  /** which pool this game was created in */
  tc: TimeControlId;
  /** false for a casual game: the result never touches either rating */
  rateable: boolean;
  /** Elo ratings at the time of the snapshot. */
  ratings: { red: number; black: number };
  /** Rating change applied when the game finished. */
  ratingDelta?: { red: number; black: number };
}

// ---- notifications & messages ----
export type NotifyKind = "friend_request" | "friend_accepted" | "challenge" | "message";

export interface Notification {
  id: string;
  kind: NotifyKind;
  fromUser: string;
  fromName: string;
  /** short preview, for messages */
  text?: string;
  at: number;
  read: boolean;
}

export interface DirectMessage {
  id: string;
  from: string;
  to: string;
  text: string;
  at: number;
  read: boolean;
}

export interface LeaderRow {
  rank: number;
  name: string;
  /** account username, for linking to the public profile; absent for guests */
  user?: string;
  rating: number;
  games: number;
  wins: number;
  losses: number;
  draws: number;
}

// ---- Client -> Server ----
export type ClientMsg =
  | { t: "hello"; playerId: string; name?: string; token?: string }
  | { t: "queue"; tc?: TimeControlId; rated?: boolean } // quick pair: auto-match, else sit in the lobby
  | { t: "cancelQueue" }
  | { t: "postSeek"; tc: TimeControlId; rated: boolean; color: ColorPref }
  | { t: "acceptSeek"; id: string }
  | { t: "cancelSeek" }
  | { t: "lobby"; on: boolean } // subscribe/unsubscribe from lobby updates
  | { t: "createRoom"; tc?: TimeControlId }
  | { t: "joinRoom"; code: string }
  | { t: "move"; gameId: string; from: number; to: number }
  | { t: "resign"; gameId: string }
  | { t: "rematch"; gameId: string }
  | { t: "offerDraw"; gameId: string }
  | { t: "acceptDraw"; gameId: string }
  | { t: "declineDraw"; gameId: string }
  | { t: "leave"; gameId: string }
  | { t: "leaderboard" }
  // Direct challenge between friends (both must be signed-in accounts).
  | { t: "challenge"; to: string; tc?: TimeControlId } // to = friend username
  | { t: "acceptChallenge"; id: string }
  | { t: "declineChallenge"; id: string }
  | { t: "cancelChallenge"; id: string }
  // ---- spectating -------------------------------------------------------
  /** Follow a game you are not playing in. Read-only: the server never accepts
   *  a move, resign or draw offer from a watcher. */
  | { t: "watch"; gameId: string }
  | { t: "unwatch"; gameId: string }
  /** Subscribe to the list of games in progress (the Watch page). */
  | { t: "liveGames"; on: boolean }
  // ---- table talk -------------------------------------------------------
  /** A message in the game's own chat. Players and watchers may both speak. */
  | { t: "say"; gameId: string; text: string }
  /** "still typing" ping. Sent while composing, never stored. */
  | { t: "typing"; gameId: string };

// ---- Server -> Client ----
export type ServerMsg =
  | { t: "welcome"; playerId: string; account?: { username: string; displayName: string; rating: number } | null }
  | { t: "queued" }
  | { t: "queueLeft" }
  | { t: "lobby"; seeks: Seek[]; online: number; playing: number }
  | { t: "room"; code: string; tc: TimeControlId }
  | { t: "start"; snapshot: GameSnapshot }
  | { t: "state"; snapshot: GameSnapshot }
  | { t: "illegal"; gameId: string; reason: string }
  | { t: "opponentLeft"; gameId: string }
  | { t: "opponentJoined"; gameId: string }
  | { t: "rematchOffered"; gameId: string }
  | { t: "drawOffered"; gameId: string }
  | { t: "drawDeclined"; gameId: string }
  /** `code` is a stable machine-readable reason where one exists
   *  (currently only "email_unverified"); `msg` is what a person reads. */
  | { t: "error"; msg: string; code?: string }
  | { t: "leaderboard"; rows: LeaderRow[] }
  // ---- spectating -------------------------------------------------------
  /** The position, for a watcher. `watching: true` marks the board read-only. */
  | { t: "watching"; snapshot: GameSnapshot; watchers: number }
  /** How many people are following this game, pushed when it changes. */
  | { t: "watchers"; gameId: string; n: number }
  /** The games in progress that anyone may watch. */
  | { t: "liveGames"; games: LiveGame[] }
  // ---- table talk -------------------------------------------------------
  | { t: "said"; gameId: string; from: string; name: string; text: string; at: number; watcher: boolean }
  /** What was said at this table before you (re)joined it: on reconnect, and to a new watcher. */
  | { t: "chatLog"; gameId: string; lines: { from: string; name: string; text: string; at: number; watcher: boolean }[] }
  | { t: "typing"; gameId: string; name: string }
  // Direct-challenge lifecycle.
  | { t: "challenged"; id: string; fromUser: string; fromName: string; tc: TimeControlId }
  | { t: "challengeSent"; id: string; toUser: string }
  | { t: "challengeDeclined"; id: string }
  | { t: "challengeCanceled"; id: string }
  | { t: "friendsUpdate" } // hint the client to refetch its friend graph
  // A thing happened while you were elsewhere on the site.
  | { t: "notify"; n: Notification; unread: number }
  // Someone wrote to you; `unread` counts unread messages, not notifications.
  | { t: "dm"; m: DirectMessage; unread: number };
